import React, { useState, useEffect, useCallback } from "react";
import { Container, Spinner, Row, Col } from "react-bootstrap";
import { Link } from "react-router-dom";
import {
  LineChart, Line, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from "recharts";
import { database } from "../Firebase";
import {
  collection, query, where, getDocs, orderBy
} from "firebase/firestore";

const PERIODES = [
  { key: 'today',   label: "Aujourd'hui" },
  { key: '7days',   label: '7 jours'     },
  { key: '30days',  label: '30 jours'    },
  { key: '3months', label: '3 mois'      },
  { key: '6months', label: '6 mois'      },
  { key: '1year',   label: '1 an'        },
  { key: 'all',     label: 'Tout'        },
];

const getDateDebut = (periode) => {
  const now = new Date();
  switch (periode) {
    case 'today':   { const d = new Date(); d.setHours(0,0,0,0); return d; }
    case '7days':   return new Date(now.getTime() - 7   * 24 * 60 * 60 * 1000);
    case '30days':  return new Date(now.getTime() - 30  * 24 * 60 * 60 * 1000);
    case '3months': return new Date(now.getTime() - 90  * 24 * 60 * 60 * 1000);
    case '6months': return new Date(now.getTime() - 180 * 24 * 60 * 60 * 1000);
    case '1year':   return new Date(now.getTime() - 365 * 24 * 60 * 60 * 1000);
    default:        return new Date(0);
  }
};

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div style={{
      background: '#fff', border: '1px solid #dee2e6',
      borderRadius: '8px', padding: '10px 14px', fontSize: '0.82rem',
      boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
    }}>
      <p style={{ fontWeight: 700, marginBottom: '4px' }}>{label}</p>
      {payload.map((p, i) => (
        <p key={i} style={{ color: p.color, margin: '2px 0' }}>
          {p.name} : <strong>{p.value}</strong>
        </p>
      ))}
    </div>
  );
};

export default function AdminDashboard() {
  const [periode,           setPeriode]           = useState('today');
  const [loading,           setLoading]           = useState(true);
  const [lastUpdate,        setLastUpdate]         = useState(null);
  const [statsPionniers,    setStatsPionniers]    = useState(null);
  const [statsSignalements, setStatsSignalements] = useState(null);
  const [statsMessages,     setStatsMessages]     = useState(null);
  const [statsSuggestions,  setStatsSuggestions]  = useState(null);
  const [statsQualite,      setStatsQualite]      = useState(null);
  const [statsAvertissements, setStatsAvertissements] = useState(null);
  const [statsTypes,        setStatsTypes]        = useState(null);

  const [graphParHeure,     setGraphParHeure]     = useState([]);
  const [graphParJour,      setGraphParJour]      = useState([]);
  const [graphQualite,      setGraphQualite]      = useState([]);
  const [graphRaisons,      setGraphRaisons]      = useState([]);
  const [graphTypes,        setGraphTypes]        = useState([]);

  const chargerStats = useCallback(async () => {
    setLoading(true);
    const dateDebut      = getDateDebut(periode);
    const timestampDebut = dateDebut.getTime();

    try {
      // ── 1. Users ──────────────────────────────────────────────────────────
      const pioneersSnap = await getDocs(query(
        collection(database, 'users'),
        where('estPionnier', '==', true)
      ));
      const pionniers  = pioneersSnap.docs.map(d => ({ id: d.id, ...d.data() }));
      const usersSnap  = await getDocs(collection(database, 'users'));
      const usersTotal = usersSnap.docs.map(d => ({ id: d.id, ...d.data() }));
      const usersPeriode = periode === 'all'
        ? usersTotal
        : usersTotal.filter(u => {
            if (!u.dateCreation) return false;
            return new Date(u.dateCreation).getTime() >= timestampDebut;
          });

      // Users suspendus
      const usersSuspendus = usersTotal.filter(u =>
        u.suspendu && u.suspendJusqu > Date.now()
      );

      // ── 2. Signalements ───────────────────────────────────────────────────
      const spotsSnap = await getDocs(collection(database, 'historique_signalements'));
      const spotsAll  = spotsSnap.docs.map(d => d.data());
      const spots     = periode === 'all'
        ? spotsAll
        : spotsAll.filter(s => s.timestamp >= timestampDebut);

      // Types de places
      const typesMap = spots.reduce((acc, s) => {
        const t = s.typePlace || 'exterieur';
        acc[t]  = (acc[t] || 0) + 1;
        return acc;
      }, {});

      const labelsTypes = {
        exterieur:        '🌤️ Extérieur',
        personnel:        '🏥 Personnel',
        sous_sol:         '⬇️ Sous-sol',
        etage:            '⬆️ Étage',
        borne_electrique: '⚡ Borne',
      };

      const graphTypesData = Object.entries(typesMap).map(([key, value]) => ({
        name:  labelsTypes[key] || key,
        value,
      }));

      // ── 3. Suggestions ────────────────────────────────────────────────────
      const suggestionsSnap = await getDocs(
        query(collection(database, 'suggestions'), orderBy('dateCreation', 'desc'))
      );
      const suggestionsAll = suggestionsSnap.docs.map(d => ({ id: d.id, ...d.data() }));
      const suggestions    = periode === 'all'
        ? suggestionsAll
        : suggestionsAll.filter(s => {
            if (!s.dateCreation) return false;
            const ts = s.dateCreation?.toDate?.()?.getTime() || new Date(s.dateCreation).getTime();
            return ts >= timestampDebut;
          });
      const noteMoyenne    = suggestions.length > 0
        ? (suggestions.reduce((acc, sg) => acc + (sg.note || 0), 0) / suggestions.length).toFixed(1)
        : '—';
      const sugNouveaux    = suggestions.filter(s => s.statut === 'nouveau').length;
      const dernSuggestions = suggestions.slice(0, 5);

      // ── 4. Messages contact ───────────────────────────────────────────────
      const messagesSnap = await getDocs(collection(database, 'messages_contact'));
      const messagesAll  = messagesSnap.docs.map(d => ({ id: d.id, ...d.data() }));
      const messages     = periode === 'all'
        ? messagesAll
        : messagesAll.filter(m => {
            if (!m.dateEnvoi) return false;
            const ts = m.dateEnvoi?.toDate?.()?.getTime() || new Date(m.dateEnvoi).getTime();
            return ts >= timestampDebut;
          });
      const msgNouveaux = messages.filter(m => m.statut === 'nouveau').length;
      const msgTraites  = messages.filter(m => m.statut === 'traite').length;

      // ── 5. Feedback parking ───────────────────────────────────────────────
      const feedbackSnap = await getDocs(collection(database, 'parkingFeedback'));
      const feedbacksAll = feedbackSnap.docs.map(d => d.data());
      const feedbacks    = periode === 'all'
        ? feedbacksAll
        : feedbacksAll.filter(f => f.timestamp >= timestampDebut);

      const fbSuccess      = feedbacks.filter(f => f.success === true).length;
      const fbFailed       = feedbacks.filter(f => f.success === false).length;
      const fbPlaceOccupee = feedbacks.filter(f => f.raison === 'place_occupee').length;
      const fbMauvaisType  = feedbacks.filter(f => f.raison === 'mauvais_type').length;
      const fbFauxSignal   = feedbacks.filter(f => f.raison === 'faux_signalement').length;
      const tauxSucces     = feedbacks.length > 0
        ? Math.round((fbSuccess / feedbacks.length) * 100) : 0;

      // ── 6. Avertissements ─────────────────────────────────────────────────
      const avertSnap = await getDocs(collection(database, 'avertissements'));
      const avertsAll = avertSnap.docs.map(d => d.data());
      const averts    = periode === 'all'
        ? avertsAll
        : avertsAll.filter(a => a.timestamp >= timestampDebut);

      const avertMauvaisType  = averts.filter(a => a.raison === 'mauvais_type').length;
      const avertFauxSignal   = averts.filter(a => a.raison === 'faux_signalement').length;

      // ── Graphiques ────────────────────────────────────────────────────────
      const heures = Array.from({ length: 24 }, (_, h) => ({
        heure:        `${String(h).padStart(2, '0')}h`,
        signalements: 0,
        inscriptions: 0,
        messages:     0,
      }));
      spots.forEach(s => { const h = new Date(s.timestamp).getHours(); heures[h].signalements++; });
      usersPeriode.forEach(u => {
        if (!u.dateCreation) return;
        heures[new Date(u.dateCreation).getHours()].inscriptions++;
      });
      messages.forEach(m => {
        if (!m.dateEnvoi) return;
        const ts = m.dateEnvoi?.toDate?.() || new Date(m.dateEnvoi);
        heures[ts.getHours()].messages++;
      });
      setGraphParHeure(heures);

      const parJourMap = {};
      const addToDay = (timestamp, key) => {
        if (!timestamp) return;
        const date = new Date(timestamp).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' });
        if (!parJourMap[date]) parJourMap[date] = { date, signalements: 0, inscriptions: 0, messages: 0 };
        parJourMap[date][key]++;
      };
      spots.forEach(s => addToDay(s.timestamp, 'signalements'));
      usersPeriode.forEach(u => u.dateCreation && addToDay(new Date(u.dateCreation).getTime(), 'inscriptions'));
      messages.forEach(m => {
        if (!m.dateEnvoi) return;
        addToDay(m.dateEnvoi?.toDate?.()?.getTime() || new Date(m.dateEnvoi).getTime(), 'messages');
      });
      setGraphParJour(Object.values(parJourMap).sort((a, b) => {
        const [ad, am] = a.date.split('/').map(Number);
        const [bd, bm] = b.date.split('/').map(Number);
        return am !== bm ? am - bm : ad - bd;
      }));

      setGraphQualite([
        { name: 'Places trouvées', value: fbSuccess },
        { name: 'Places manquées', value: fbFailed  },
      ].filter(g => g.value > 0));

      setGraphRaisons([
        { name: '🚗 Place occupée',    value: fbPlaceOccupee },
        { name: '📍 Mauvais type',     value: fbMauvaisType  },
        { name: '⚠️ Faux signalement', value: fbFauxSignal   },
      ].filter(g => g.value > 0));

      setGraphTypes(graphTypesData);

      // ── Mise à jour des états ─────────────────────────────────────────────
      setStatsPionniers({
        total: pionniers.length, objectif: 100,
        parSecteur: pionniers.reduce((acc, p) => {
          const s = p.secteurPionnier || 'Non précisé';
          acc[s]  = (acc[s] || 0) + 1;
          return acc;
        }, {}),
      });
      setStatsSignalements({
        total: spots.length, nouveauxUsers: usersPeriode.length,
        totalUsers: usersTotal.length, fbSuccess, fbFailed,
        tauxSucces, totalFeedback: feedbacks.length,
      });
      setStatsQualite({
        fbSuccess, fbFailed, tauxSucces,
        totalFeedback: feedbacks.length,
        fbPlaceOccupee, fbMauvaisType, fbFauxSignal,
      });
      setStatsAvertissements({
        total:      averts.length,
        mauvaisType: avertMauvaisType,
        fauxSignal:  avertFauxSignal,
        suspendus:   usersSuspendus.length,
      });
      setStatsTypes({
        total:   spots.length,
        typesMap,
      });
      setStatsSuggestions({
        total: suggestions.length, nouveaux: sugNouveaux,
        noteMoyenne, dernieres: dernSuggestions,
      });
      setStatsMessages({ total: messages.length, nouveaux: msgNouveaux, traites: msgTraites });
      setLastUpdate(new Date().toLocaleTimeString('fr-FR'));

    } catch (err) {
      console.error('Erreur chargement stats:', err);
    } finally {
      setLoading(false);
    }
  }, [periode]);

  useEffect(() => { chargerStats(); }, [chargerStats]);

  const MetricCard = ({ icon, label, value, sub, color = '#07a993', bg = '#e8f8f5' }) => (
    <div style={{
      background: bg, border: `1px solid ${color}33`,
      borderRadius: '12px', padding: '16px', textAlign: 'center',
    }}>
      <div style={{ fontSize: '1.8rem', marginBottom: '4px' }}>{icon}</div>
      <div style={{ fontSize: '0.75rem', color: '#6c757d', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '4px' }}>
        {label}
      </div>
      <div style={{ fontSize: '1.8rem', fontWeight: 700, color, lineHeight: 1 }}>{value}</div>
      {sub && <div style={{ fontSize: '0.75rem', color: '#6c757d', marginTop: '4px' }}>{sub}</div>}
    </div>
  );

  const labelPeriode       = PERIODES.find(p => p.key === periode)?.label || '';
  const COLORS_QUALITE     = ['#07a993', '#dc3545'];
  const COLORS_RAISONS     = ['#6c757d', '#0d6efd', '#dc3545'];
  const COLORS_TYPES       = ['#07a993', '#e74c3c', '#3498db', '#9b59b6', '#f39c12'];

  if (loading) {
    return (
      <Container className="py-5 text-center">
        <Spinner animation="border" style={{ color: '#07a993' }} />
        <p className="mt-2 text-muted">Chargement du tableau de bord...</p>
      </Container>
    );
  }

  return (
    <Container fluid className="py-4 px-3 px-md-4" style={{ maxWidth: '1100px' }}>

      {/* ── En-tête ─────────────────────────────────────────────────────── */}
      <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
        <div>
          <h2 style={{ fontWeight: 700, marginBottom: '2px', fontSize: 'clamp(1.2rem,4vw,1.6rem)' }}>
            🏥 Dashboard POC — Hôpital André Mignot
          </h2>
          {lastUpdate && (
            <span style={{ fontSize: '0.8rem', color: '#6c757d' }}>Mise à jour : {lastUpdate}</span>
          )}
        </div>
        <button onClick={chargerStats} style={{
          padding: '8px 16px', borderRadius: '8px',
          border: '2px solid #dee2e6', background: '#fff',
          fontSize: '0.85rem', cursor: 'pointer', fontWeight: 600,
        }}>
          🔄 Actualiser
        </button>
      </div>

      {/* ── Sélecteur de période ─────────────────────────────────────────── */}
      <div style={{ background: '#f8f9fa', borderRadius: '12px', padding: '12px 16px', marginBottom: '16px', border: '1px solid #dee2e6' }}>
        <div style={{ fontSize: '0.78rem', fontWeight: 600, color: '#6c757d', marginBottom: '8px' }}>Période d'analyse :</div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
          {PERIODES.map(({ key, label }) => (
            <button key={key} onClick={() => setPeriode(key)} style={{
              padding: '6px 14px', borderRadius: '20px', border: '2px solid',
              borderColor: periode === key ? '#07a993' : '#dee2e6',
              background:  periode === key ? '#07a993' : '#fff',
              color:       periode === key ? '#fff'    : '#555',
              fontWeight:  periode === key ? 700       : 400,
              fontSize: '0.82rem', cursor: 'pointer', minHeight: '36px',
            }}>
              {label}
            </button>
          ))}
        </div>
      </div>

      {/* ── Bandeau période active ───────────────────────────────────────── */}
      <div style={{
        background: 'linear-gradient(135deg, #07a993 0%, #0d6efd 100%)',
        color: '#fff', borderRadius: '8px', padding: '8px 16px',
        marginBottom: '20px', fontSize: '0.85rem', fontWeight: 600,
        display: 'flex', alignItems: 'center', gap: '8px',
      }}>
        📅 Résultats : <strong>{labelPeriode}</strong>
        {periode !== 'all' && (
          <span style={{ opacity: 0.85, fontWeight: 400 }}>
            — depuis le {getDateDebut(periode).toLocaleDateString('fr-FR')}
          </span>
        )}
      </div>

      {/* ══════════════════════════════════════════════════════════════════
          SECTION 1 — ADOPTION
      ══════════════════════════════════════════════════════════════════ */}
      <h5 style={{ fontWeight: 700, marginBottom: '12px' }}>📊 Adoption</h5>
      <Row className="g-3 mb-4">
        <Col xs={6} md={3}>
          <MetricCard icon="👥" label="Utilisateurs total"
            value={statsSignalements?.totalUsers || 0}
            color="#07a993" bg="#e8f8f5" />
        </Col>
        <Col xs={6} md={3}>
          <MetricCard icon="🆕" label="Nouveaux inscrits"
            value={statsSignalements?.nouveauxUsers || 0}
            sub={labelPeriode} color="#0d6efd" bg="#e8f0fe" />
        </Col>
        <Col xs={6} md={3}>
          <MetricCard icon="🏅" label="Pionniers"
            value={`${statsPionniers?.total || 0}/100`}
            sub="objectif global" color="#fd7e14" bg="#fff3e0" />
        </Col>
        <Col xs={6} md={3}>
          <MetricCard icon="📍" label="Signalements"
            value={statsSignalements?.total || 0}
            sub={labelPeriode} color="#198754" bg="#d1e7dd" />
        </Col>
      </Row>

      {/* ══════════════════════════════════════════════════════════════════
          SECTION 2 — QUALITÉ SERVICE
      ══════════════════════════════════════════════════════════════════ */}
      <h5 style={{ fontWeight: 700, marginBottom: '12px' }}>✅ Qualité du service</h5>
      <Row className="g-3 mb-4">
        <Col xs={6} md={3}>
          <MetricCard icon="✅" label="Places trouvées"
            value={statsQualite?.fbSuccess || 0}
            sub={labelPeriode} color="#198754" bg="#d1e7dd" />
        </Col>
        <Col xs={6} md={3}>
          <MetricCard icon="❌" label="Places manquées"
            value={statsQualite?.fbFailed || 0}
            sub={labelPeriode} color="#dc3545" bg="#f8d7da" />
        </Col>
        <Col xs={6} md={3}>
          <MetricCard icon="🎯" label="Taux de succès"
            value={`${statsQualite?.tauxSucces || 0}%`}
            sub={`${statsQualite?.totalFeedback || 0} retours`}
            color="#07a993" bg="#e8f8f5" />
        </Col>
        <Col xs={6} md={3}>
          <MetricCard icon="⭐" label="Note moyenne app"
            value={`${statsSuggestions?.noteMoyenne || '—'}/5`}
            sub={`${statsSuggestions?.total || 0} avis`}
            color="#ffc107" bg="#fff3cd" />
        </Col>
      </Row>

      {/* ══════════════════════════════════════════════════════════════════
          SECTION 3 — AVERTISSEMENTS & MODÉRATION
      ══════════════════════════════════════════════════════════════════ */}
      <h5 style={{ fontWeight: 700, marginBottom: '12px' }}>🛡️ Modération</h5>
      <Row className="g-3 mb-4">
        <Col xs={6} md={3}>
          <MetricCard icon="⚠️" label="Avertissements"
            value={statsAvertissements?.total || 0}
            sub={labelPeriode} color="#fd7e14" bg="#fff3e0" />
        </Col>
        <Col xs={6} md={3}>
          <MetricCard icon="📍" label="Mauvais type"
            value={statsAvertissements?.mauvaisType || 0}
            sub="place mal identifiée" color="#0d6efd" bg="#e8f0fe" />
        </Col>
        <Col xs={6} md={3}>
          <MetricCard icon="🚫" label="Faux signalements"
            value={statsAvertissements?.fauxSignal || 0}
            sub="pas une place" color="#dc3545" bg="#f8d7da" />
        </Col>
        <Col xs={6} md={3}>
          <MetricCard icon="🔒" label="Users suspendus"
            value={statsAvertissements?.suspendus || 0}
            sub="actuellement" color="#6c757d" bg="#f8f9fa" />
        </Col>
      </Row>

      {/* ── Raisons des échecs ────────────────────────────────────────────── */}
      {graphRaisons.length > 0 && (
        <div className="p-3 mb-4" style={{
          background: '#fff', borderRadius: '12px',
          border: '1px solid #dee2e6',
          boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
        }}>
          <h5 style={{ fontWeight: 600, marginBottom: '8px' }}>
            🔍 Raisons des places non trouvées — {labelPeriode}
          </h5>
          <Row className="g-2">
            {[
              { label: '🚗 Place déjà occupée',              value: statsQualite?.fbPlaceOccupee || 0, color: '#6c757d', bg: '#f8f9fa' },
              { label: '📍 Place mal identifiée',            value: statsQualite?.fbMauvaisType  || 0, color: '#0d6efd', bg: '#e8f0fe' },
              { label: '⚠️ Pas une place de stationnement',  value: statsQualite?.fbFauxSignal   || 0, color: '#dc3545', bg: '#f8d7da' },
            ].map(({ label, value, color, bg }) => (
              <Col xs={12} md={4} key={label}>
                <div className="d-flex justify-content-between align-items-center p-2 rounded" style={{ background: bg }}>
                  <span style={{ fontSize: '0.85rem', fontWeight: 500, color }}>{label}</span>
                  <span style={{
                    background: color, color: '#fff',
                    padding: '4px 12px', borderRadius: '20px',
                    fontWeight: 700, fontSize: '0.88rem',
                  }}>
                    {value}
                  </span>
                </div>
              </Col>
            ))}
          </Row>
        </div>
      )}

      {/* ══════════════════════════════════════════════════════════════════
          SECTION 4 — TYPES DE PLACES
      ══════════════════════════════════════════════════════════════════ */}
      <h5 style={{ fontWeight: 700, marginBottom: '12px' }}>🅿️ Types de places signalées</h5>
      <Row className="g-3 mb-4">
        <Col xs={12} md={5}>
          <div className="p-3 h-100" style={{
            background: '#fff', borderRadius: '12px',
            border: '1px solid #dee2e6',
            boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
          }}>
            <h5 style={{ fontWeight: 600, marginBottom: '16px' }}>Répartition</h5>
            {graphTypes.length > 0 ? (
              <ResponsiveContainer width="100%" height={220}>
                <PieChart>
                  <Pie
                    data={graphTypes}
                    cx="50%" cy="50%"
                    innerRadius={50} outerRadius={85}
                    paddingAngle={3} dataKey="value"
                    label={({ name, percent }) => `${name} ${(percent*100).toFixed(0)}%`}
                    labelLine={false}
                  >
                    {graphTypes.map((_, index) => (
                      <Cell key={index} fill={COLORS_TYPES[index % COLORS_TYPES.length]} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(val) => [`${val} place(s)`, '']} />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <p className="text-muted text-center mt-4" style={{ fontSize: '0.88rem' }}>
                Aucun signalement sur cette période.
              </p>
            )}
          </div>
        </Col>
        <Col xs={12} md={7}>
          <div className="p-3 h-100" style={{
            background: '#fff', borderRadius: '12px',
            border: '1px solid #dee2e6',
            boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
          }}>
            <h5 style={{ fontWeight: 600, marginBottom: '16px' }}>Détail par type</h5>
            <div className="d-flex flex-column gap-2">
              {[
                { label: '🌤️ Extérieur',        key: 'exterieur',        color: '#07a993', bg: '#e8f8f5' },
                { label: '🏥 Personnel',          key: 'personnel',        color: '#e74c3c', bg: '#f8d7da' },
                { label: '⬇️ Sous-sol',          key: 'sous_sol',         color: '#3498db', bg: '#e8f0fe' },
                { label: '⬆️ Étage',             key: 'etage',            color: '#9b59b6', bg: '#f3e8fe' },
                { label: '⚡ Borne électrique',   key: 'borne_electrique', color: '#f39c12', bg: '#fff3cd' },
              ].map(({ label, key, color, bg }) => (
                <div key={key}
                  className="d-flex justify-content-between align-items-center p-2 rounded"
                  style={{ background: bg }}>
                  <span style={{ fontSize: '0.88rem', fontWeight: 500, color }}>{label}</span>
                  <span style={{
                    background: color, color: '#fff',
                    padding: '4px 12px', borderRadius: '20px',
                    fontWeight: 700, fontSize: '0.88rem',
                  }}>
                    {statsTypes?.typesMap?.[key] || 0}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </Col>
      </Row>

      {/* ══════════════════════════════════════════════════════════════════
          GRAPHIQUE 1 — Activité par heure
      ══════════════════════════════════════════════════════════════════ */}
      <div className="p-3 mb-4" style={{
        background: '#fff', borderRadius: '12px',
        border: '1px solid #dee2e6',
        boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
      }}>
        <h5 style={{ fontWeight: 600, marginBottom: '16px' }}>
          ⏱️ Activité par heure — {labelPeriode}
        </h5>
        <ResponsiveContainer width="100%" height={260}>
          <LineChart data={graphParHeure} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
            <XAxis dataKey="heure" tick={{ fontSize: 10 }} interval={2} />
            <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
            <Tooltip content={<CustomTooltip />} />
            <Legend wrapperStyle={{ fontSize: '12px' }} />
            <Line type="monotone" dataKey="signalements" name="Signalements"
              stroke="#07a993" strokeWidth={2} dot={false} activeDot={{ r: 5 }} />
            <Line type="monotone" dataKey="inscriptions" name="Inscriptions"
              stroke="#cdbe18" strokeWidth={2} dot={false} activeDot={{ r: 5 }} />
            <Line type="monotone" dataKey="messages" name="Messages"
              stroke="#0d6efd" strokeWidth={2} dot={false} activeDot={{ r: 5 }} />
          </LineChart>
        </ResponsiveContainer>
      </div>

      {/* ══════════════════════════════════════════════════════════════════
          GRAPHIQUE 2 — Évolution par jour
      ══════════════════════════════════════════════════════════════════ */}
      {graphParJour.length > 0 && (
        <div className="p-3 mb-4" style={{
          background: '#fff', borderRadius: '12px',
          border: '1px solid #dee2e6',
          boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
        }}>
          <h5 style={{ fontWeight: 600, marginBottom: '16px' }}>
            📈 Évolution sur la période — {labelPeriode}
          </h5>
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={graphParJour} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey="date" tick={{ fontSize: 10 }} />
              <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
              <Tooltip content={<CustomTooltip />} />
              <Legend wrapperStyle={{ fontSize: '12px' }} />
              <Bar dataKey="signalements" name="Signalements" fill="#07a993" radius={[4,4,0,0]} />
              <Bar dataKey="inscriptions" name="Inscriptions" fill="#cdbe18" radius={[4,4,0,0]} />
              <Bar dataKey="messages"     name="Messages"     fill="#0d6efd" radius={[4,4,0,0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* ══════════════════════════════════════════════════════════════════
          GRAPHIQUE 3 — Qualité + Raisons
      ══════════════════════════════════════════════════════════════════ */}
      <Row className="g-3 mb-4">
        <Col xs={12} md={6}>
          <div className="p-3 h-100" style={{
            background: '#fff', borderRadius: '12px',
            border: '1px solid #dee2e6',
            boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
          }}>
            <h5 style={{ fontWeight: 600, marginBottom: '16px' }}>🎖️ Qualité du service</h5>
            {graphQualite.length > 0 ? (
              <ResponsiveContainer width="100%" height={220}>
                <PieChart>
                  <Pie
                    data={graphQualite}
                    cx="50%" cy="50%"
                    innerRadius={55} outerRadius={90}
                    paddingAngle={3} dataKey="value"
                    label={({ name, percent }) => `${name} ${(percent*100).toFixed(0)}%`}
                    labelLine={false}
                  >
                    {graphQualite.map((_, index) => (
                      <Cell key={index} fill={COLORS_QUALITE[index % COLORS_QUALITE.length]} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(val) => [`${val} retour(s)`, '']} />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <p className="text-muted text-center mt-4" style={{ fontSize: '0.88rem' }}>
                Aucun feedback sur cette période.
              </p>
            )}
          </div>
        </Col>

        <Col xs={12} md={6}>
          <div className="p-3 h-100" style={{
            background: '#fff', borderRadius: '12px',
            border: '1px solid #dee2e6',
            boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
          }}>
            <h5 style={{ fontWeight: 600, marginBottom: '16px' }}>🔍 Raisons des échecs</h5>
            {graphRaisons.length > 0 ? (
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={graphRaisons} layout="vertical"
                  margin={{ top: 5, right: 30, left: 10, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis type="number" tick={{ fontSize: 11 }} allowDecimals={false} />
                  <YAxis type="category" dataKey="name" tick={{ fontSize: 10 }} width={140} />
                  <Tooltip content={<CustomTooltip />} />
                  <Bar dataKey="value" name="Occurrences" radius={[0,4,4,0]}>
                    {graphRaisons.map((_, index) => (
                      <Cell key={index} fill={COLORS_RAISONS[index % COLORS_RAISONS.length]} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <p className="text-muted text-center mt-4" style={{ fontSize: '0.88rem' }}>
                Aucun échec sur cette période.
              </p>
            )}
          </div>
        </Col>
      </Row>

      {/* ══════════════════════════════════════════════════════════════════
          SECTION 5 — PIONNIERS
      ══════════════════════════════════════════════════════════════════ */}
      <h5 style={{ fontWeight: 700, marginBottom: '12px' }}>🏅 Programme Pionniers</h5>
      <div className="p-3 rounded mb-4" style={{
        background: '#fff', border: '1px solid #dee2e6',
        boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
      }}>
        <div className="d-flex justify-content-between mb-1" style={{ fontSize: '0.85rem', fontWeight: 600 }}>
          <span>Progression pionniers</span>
          <span style={{ color: '#07a993' }}>{statsPionniers?.total || 0} / 100</span>
        </div>
        <div style={{
          background: '#dee2e6', borderRadius: '20px',
          height: '12px', marginBottom: '16px', overflow: 'hidden',
        }}>
          <div style={{
            width: `${Math.min((statsPionniers?.total || 0), 100)}%`,
            background: 'linear-gradient(135deg, #07a993, #0d6efd)',
            height: '100%', borderRadius: '20px', transition: 'width 0.5s ease',
          }} />
        </div>
        {statsPionniers?.parSecteur && Object.keys(statsPionniers.parSecteur).length > 0 ? (
          <>
            <div style={{ fontWeight: 600, fontSize: '0.88rem', marginBottom: '8px' }}>
              Répartition par secteur :
            </div>
            {Object.entries(statsPionniers.parSecteur).map(([secteur, count]) => (
              <div key={secteur} className="d-flex justify-content-between align-items-center mb-2">
                <span style={{ fontSize: '0.85rem' }}>📍 {secteur}</span>
                <span style={{ fontWeight: 700, color: '#07a993', fontSize: '0.88rem' }}>
                  {count} pionnier{count > 1 ? 's' : ''}
                </span>
              </div>
            ))}
          </>
        ) : (
          <p style={{ color: '#6c757d', fontSize: '0.85rem', margin: 0 }}>
            Aucun pionnier inscrit pour le moment.
          </p>
        )}
      </div>

      {/* ══════════════════════════════════════════════════════════════════
          SECTION 6 — SUGGESTIONS + MESSAGES
      ══════════════════════════════════════════════════════════════════ */}
      <Row className="g-3 mb-4">
        <Col xs={12} md={6}>
          <div className="p-3 rounded h-100" style={{
            background: '#fff', border: '1px solid #dee2e6',
            boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
          }}>
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h5 style={{ fontWeight: 700, margin: 0 }}>💡 Suggestions</h5>
              <Link to="/admin/suggestions" style={{ fontSize: '0.82rem', color: '#07a993', fontWeight: 600 }}>
                Voir tout →
              </Link>
            </div>
            <div style={{ fontSize: '0.75rem', color: '#6c757d', marginBottom: '12px', fontStyle: 'italic' }}>
              Période : {labelPeriode}
            </div>
            <Row className="g-2 mb-3">
              {[
                { label: 'Total',    value: statsSuggestions?.total    || 0, bg: '#f8f9fa', color: '#07a993' },
                { label: 'Nouveaux', value: statsSuggestions?.nouveaux || 0, bg: '#fce8e8', color: '#dc3545' },
                { label: 'Note /5',  value: statsSuggestions?.noteMoyenne || '—', bg: '#fff3cd', color: '#ffc107' },
              ].map(({ label, value, bg, color }) => (
                <Col xs={4} key={label}>
                  <div className="text-center p-2 rounded" style={{ background: bg }}>
                    <div style={{ fontSize: '1.4rem', fontWeight: 700, color }}>{value}</div>
                    <div style={{ fontSize: '0.72rem', color: '#6c757d' }}>{label}</div>
                  </div>
                </Col>
              ))}
            </Row>
            {statsSuggestions?.dernieres?.length > 0 ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                {statsSuggestions.dernieres.map(s => (
                  <div key={s.id} style={{
                    padding: '8px 10px', borderRadius: '8px',
                    background: '#f8f9fa', fontSize: '0.82rem',
                    borderLeft: `3px solid ${
                      s.categorie === 'bug'        ? '#dc3545' :
                      s.categorie === 'suggestion' ? '#0d6efd' :
                      s.categorie === 'compliment' ? '#198754' : '#6c757d'
                    }`,
                  }}>
                    <div className="d-flex justify-content-between">
                      <span style={{ fontWeight: 600 }}>
                        {s.categorie === 'bug' ? '🐛' :
                         s.categorie === 'suggestion' ? '💡' :
                         s.categorie === 'compliment' ? '❤️' : '💬'} {s.pseudo || 'Anonyme'}
                      </span>
                      <span style={{ color: '#ffc107' }}>{'★'.repeat(s.note || 0)}</span>
                    </div>
                    <div style={{ color: '#555', marginTop: '2px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {s.description}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p style={{ color: '#6c757d', fontSize: '0.85rem', textAlign: 'center', margin: 0 }}>
                Aucune suggestion sur cette période.
              </p>
            )}
          </div>
        </Col>

        <Col xs={12} md={6}>
          <div className="p-3 rounded h-100" style={{
            background: '#fff', border: '1px solid #dee2e6',
            boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
          }}>
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h5 style={{ fontWeight: 700, margin: 0 }}>✉️ Messages</h5>
              <Link to="/admin/messages" style={{ fontSize: '0.82rem', color: '#07a993', fontWeight: 600 }}>
                Voir tout →
              </Link>
            </div>
            <div style={{ fontSize: '0.75rem', color: '#6c757d', marginBottom: '12px', fontStyle: 'italic' }}>
              Période : {labelPeriode}
            </div>
            <Row className="g-2">
              {[
                { label: 'Total',    value: statsMessages?.total    || 0, bg: '#f8f9fa', color: '#07a993' },
                { label: 'Nouveaux', value: statsMessages?.nouveaux || 0, bg: '#fce8e8', color: '#dc3545' },
                { label: 'Traités',  value: statsMessages?.traites  || 0, bg: '#d1e7dd', color: '#198754' },
              ].map(({ label, value, bg, color }) => (
                <Col xs={4} key={label}>
                  <div className="text-center p-2 rounded" style={{ background: bg }}>
                    <div style={{ fontSize: '1.4rem', fontWeight: 700, color }}>{value}</div>
                    <div style={{ fontSize: '0.72rem', color: '#6c757d' }}>{label}</div>
                  </div>
                </Col>
              ))}
            </Row>
            {(statsMessages?.nouveaux || 0) > 0 && (
              <div className="mt-3 p-2 rounded" style={{
                background: '#fce8e8', border: '1px solid #dc3545',
                fontSize: '0.82rem', textAlign: 'center',
              }}>
                🔴 <strong>{statsMessages.nouveaux}</strong> message{statsMessages.nouveaux > 1 ? 's' : ''} en attente
                {' '}<Link to="/admin/messages" style={{ color: '#dc3545', fontWeight: 700 }}>Répondre →</Link>
              </div>
            )}
            {statsMessages?.total === 0 && (
              <p style={{ color: '#6c757d', fontSize: '0.85rem', textAlign: 'center', marginTop: '16px' }}>
                Aucun message sur cette période.
              </p>
            )}
          </div>
        </Col>
      </Row>

    </Container>
  );
}